<template>
  <div class="space-y-24">
    <h1 class="heading-1">{{ i18n(translations.title) }}</h1>

    <p class="body-1">
      {{ i18n(translations.description) }}
    </p>

    <RevTextList variant="ordered">
      <RevTextListItem>
        <FormattedMessage :definition="translations.step1">
          <template #templateLink>
            <RevLink href="#" target="_blank">{{
              i18n(translations.templateLink)
            }}</RevLink>
          </template>
        </FormattedMessage>
      </RevTextListItem>

      <RevTextListItem>
        {{ i18n(translations.step2) }}
      </RevTextListItem>

      <RevTextListItem>
        {{ i18n(translations.step3) }}
      </RevTextListItem>

      <RevTextListItem>
        {{ i18n(translations.step4) }}
      </RevTextListItem>

      <RevTextListItem>
        {{ i18n(translations.step5) }}
      </RevTextListItem>
    </RevTextList>

    <RevForm
      class="space-y-24"
      :initial-values
      :validate
      @submit="handleSubmit"
    >
      <template #default="{ values, errors, hasError }">
        <RevInputFileUpload
          id="letter-of-authorization-file-input"
          v-model="values.letterOfAuthorization"
          :accept="['.pdf', '.jpg', '.jpeg', '.png']"
          :description="
            errors.letterOfAuthorization ??
            i18n(translations.fileUploadDescription)
          "
          :errors="errors.letterOfAuthorization as any"
          :label-empty="i18n(translations.fileUploadLabel)"
          :max-file-size="5 * 1024 * 1024"
          :max-files="1"
        />

        <div class="flex justify-end">
          <RevButton
            :disabled="hasError || isSaving"
            :loading="isSaving"
            type="submit"
            variant="primary"
          >
            {{ i18n(translations.saveButton) }}
          </RevButton>
        </div>
      </template>
    </RevForm>
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useHead } from '#imports'
import { computed, ref } from 'vue'

import { HttpApiError } from '@backmarket/http-api'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInputFileUpload } from '@ds/components/InputFileUpload'
import { RevLink } from '@ds/components/Link'
import { RevTextList } from '@ds/components/TextList'
import { RevTextListItem } from '@ds/components/TextListItem'

import { ROUTE_NAMES } from '~/constants/routes'
import { useKybData } from '~/scopes/kyb/composables/useKybData'

import translations from './LetterOfAuthorization.translations'

type FormValues = {
  letterOfAuthorization: File[]
}

const i18n = useI18n()
useHead({ title: i18n(translations.pageTitle) })
const { openSuccessToast, openErrorToast } = useTheToast()
const {
  letterOfAuthorization,
  updateLetterOfAuthorization,
  createLetterOfAuthorization,
  refreshKybData,
} = useKybData()

const isSaving = ref(false)

const initialValues = computed<FormValues>(() => ({
  letterOfAuthorization: [],
}))

const validate = computed(() => {
  return makeValidate<FormValues>({
    letterOfAuthorization: required(i18n(translations.fileUploadLabelRequired)),
  })
})

async function handleSubmit(formValues: FormValues) {
  const file = formValues.letterOfAuthorization[0]
  if (!file) return

  isSaving.value = true

  try {
    if (letterOfAuthorization.value) {
      await updateLetterOfAuthorization(file)
    } else {
      await createLetterOfAuthorization(file)
    }

    openSuccessToast({
      title: i18n(translations.saveSuccessTitle),
      content: i18n(translations.saveSuccessContent),
    })

    navigateTo({ name: ROUTE_NAMES.KYB.STEPS.REVIEW })
    refreshKybData()
  } catch (error) {
    const { title: content } = HttpApiError.fromAnyError(error as Error)

    openErrorToast({
      title: i18n(translations.saveError),
      content,
    })
  } finally {
    isSaving.value = false
  }
}
</script>

<style>
/* Forcing the scrollbar to always be visible to avoid horizontal shift when openint this step */
html {
  overflow-y: scroll;
}
</style>
